import { Injectable, Inject, PLATFORM_ID, Injector } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

/**
 * Détecte les sessions JWT expirées sur TOUS les appels HTTP et déclenche un
 * logout propre (effacement token + redirect login).
 *
 * Cas couverts :
 *   1. HTTP 401 / 403 (vrais codes d'erreur d'auth standards)
 *   2. HTTP 200 avec body `{status: "Token is Expired"}` — pattern Laravel
 *      JWT du backend Akces qui renvoie un 200 trompeur au lieu d'un 401.
 *   3. HTTP 200 avec body `{status: "Authorization Token not found"}` —
 *      même pattern quand le token est absent (expiré côté client).
 *
 * Sans cet interceptor, chaque page admin (dashboard / orders / refundlist…)
 * doit gérer le cas localement et affiche 0 données silencieusement quand le
 * token a expiré. L'interceptor évite cette duplication et garantit un comporte-
 * ment uniforme : dès qu'une réponse signale une session expirée, on logout.
 *
 * Anti-boucle :
 *   - On skip les requêtes vers /login/* (sinon on ferait logout pendant la
 *     reconnexion → boucle infinie).
 *   - On skip si on est déjà sur une page de login.
 *   - On ne déclenche qu'UNE fois par cycle (flag `tripped`) pour éviter
 *     plusieurs redirections empilées quand plusieurs requêtes parallèles
 *     détectent le souci en même temps.
 */
@Injectable()
export class AuthExpiredInterceptor implements HttpInterceptor {
  private tripped = false;

  /**
   * IMPORTANT : on injecte `Injector` (lazy) au lieu d'injecter directement
   * AuthService + Router. Sinon Angular détecte une circular dependency :
   *   AuthService → HttpClient → AuthExpiredInterceptor → AuthService
   * et tout le pipeline HTTP plante silencieusement (ngx-translate ne charge
   * plus les traductions, tous les `| translate` retournent la clé brute,
   * les services planent en cours de boot, etc.).
   * Le pattern lazy via Injector contourne le cycle car la résolution n'a
   * lieu qu'au moment du premier appel (après que tout soit bootstrappé).
   */
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private injector: Injector,
  ) {}

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private get router(): Router {
    return this.injector.get(Router);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // SSR : pas de localStorage / navigation → on passe sans rien faire.
    if (!isPlatformBrowser(this.platformId)) {
      return next.handle(req);
    }

    // Skip les endpoints d'auth (login, signin, refresh, handletoken) pour
    // ne pas créer de boucle : si le user tape un mauvais mot de passe et
    // le backend répond avec "Token not found", on ne doit PAS le logout.
    if (this.isAuthEndpoint(req.url)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.checkBody(event.body);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        // 401 = pas authentifié → logout systématique.
        // 403 = authentifié mais pas les droits (ex: admin-only endpoint sur
        // un orga lambda). AdminMiddleware renvoie `{error: 'FORBIDDEN'}` : ne
        // PAS logout dans ce cas — c'est un accès refusé par rôle, pas une
        // session expirée. Sinon un nouveau signup qui touche managerList se
        // fait éjecter sur /login/signin (bug signalé prod 2026-07-02).
        if (err.status === 401) {
          this.triggerLogout('HTTP 401');
        } else if (err.status === 403) {
          const forbiddenByRole = err.error
            && typeof err.error === 'object'
            && err.error.error === 'FORBIDDEN';
          if (!forbiddenByRole) {
            this.triggerLogout('HTTP 403');
          }
        }
        // Laravel peut aussi renvoyer un message d'expiration dans l'error body
        if (err.error && typeof err.error === 'object') {
          this.checkBody(err.error);
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * Vérifie le body d'une réponse pour le pattern Laravel JWT
   * `{status: "Token is Expired" | "Authorization Token not found" | ...}`
   */
  private checkBody(body: any): void {
    if (!body || typeof body !== 'object') return;
    const status = (body.status ?? body.message ?? '').toString().toLowerCase();
    if (!status) return;
    if (/token.*(expir|not.*found|invalid|absent)/i.test(status)
      || /unauthor/i.test(status)
      || status === 'token_invalid'
      || status === 'token_expired') {
      this.triggerLogout(`body.status="${status}"`);
    }
  }

  private triggerLogout(reason: string): void {
    if (this.tripped) return;
    // Si on est déjà sur une page login, on n'a rien à faire de plus
    const url = this.router.url || '';
    if (url.startsWith('/login') || url.startsWith('/signup')) return;

    this.tripped = true;
    console.warn('[AuthExpired] Session expirée détectée :', reason, '→ logout');
    // Petit delay pour laisser le composant courant terminer son tick Angular,
    // sinon le logout peut interrompre un render et provoquer des erreurs
    // "ExpressionChangedAfterChecked" dans certains composants.
    setTimeout(() => {
      try {
        this.authService.logout();
      } catch (e) {
        // En dernier recours, redirect manuel
        this.router.navigate(['/login/signin']);
      }
    }, 50);
  }

  private isAuthEndpoint(url: string): boolean {
    if (!url) return false;
    return /\/(login|signin|signup|refresh|handletoken|forgot|reset|check-token|change-password)(\/|$|\?)/i.test(url);
  }
}
